import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { RefreshCw, Sparkles, Search, AlertTriangle, Check, Info } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

// Field-aware formatter: `$` prefix only for dollar fields, plain
// separated integers for share counts. Fixes prior UI trust defect.
const DOLLAR_FIELDS = new Set([
  "min_price", "max_price", "min_avg_dollar_volume",
]);
const COUNT_FIELDS = new Set([
  "min_avg_daily_volume", "min_whole_shares_affordable",
]);
function fmt(v, k) {
  if (typeof v !== "number") return String(v ?? "—");
  if (DOLLAR_FIELDS.has(k))
    return `$${v.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  if (COUNT_FIELDS.has(k))
    return v.toLocaleString(undefined, { maximumFractionDigits: 0 });
  return v.toLocaleString(undefined, { maximumFractionDigits: 2 });
}
const money = (v) =>
  v == null ? "—" : `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const pct = (v) => (v == null ? "—" : `${Number(v).toFixed(v >= 1 ? 0 : 1)}%`);

export default function ScannerPolicyPage() {
  const [active, setActive] = useState(null);
  const [rec, setRec] = useState(null);
  const [ownerVals, setOwnerVals] = useState(null);
  const [preview, setPreview] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [why, setWhy] = useState(null);
  const [whySym, setWhySym] = useState("");
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [a, r, h] = await Promise.all([
        api.get("/scanner-policy/active"),
        api.get("/scanner-policy/recommendation"),
        api.get("/scanner-policy/versions"),
      ]);
      setActive(a.data);
      setRec(r.data);
      setOwnerVals({ ...r.data.values });
      setHistory(h.data.versions || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load");
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const runPreview = async () => {
    try {
      const { data } = await api.post("/scanner-policy/impact-preview", ownerVals);
      setPreview(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Preview failed");
    }
  };

  const activate = async () => {
    setConfirmOpen(false);
    try {
      const body = {
        ...ownerVals,
        reasoning: `Owner-approved. Engine recommended max_price=$${rec.values.max_price}.`,
        // P4 provenance: send the exact recommendation the owner is
        // looking at so the server persists it durably.
        system_recommended_values: rec.system_recommended_values || rec.values,
        capital_context: {
          account_equity: rec.portfolio_state?.account?.equity,
          available_cash: rec.portfolio_state?.account?.available_cash,
          deployable_capital: rec.portfolio_state?.deployable_capital,
          positions_count: rec.portfolio_state?.account?.positions_count,
          buckets: rec.portfolio_state?.buckets,
          policy_inputs_hash: rec.policy_inputs_hash,
          recommendation_generated_at: rec.recommendation_generated_at,
        },
      };
      const { data } = await api.put("/scanner-policy/active", body);
      toast.success(`Activated ${data.policy_version} (id ${data.id.slice(0, 8)})`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Activation failed");
    }
  };

  const lookupWhy = async () => {
    if (!whySym.trim()) return;
    try {
      const { data } = await api.get(
        `/scanner-policy/why-excluded/${whySym.trim().toUpperCase()}`);
      setWhy(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Lookup failed");
    }
  };

  const overrideFields = useMemo(() => {
    if (!ownerVals || !rec) return [];
    return Object.keys(rec.values).filter(
      (k) => JSON.stringify(rec.values[k]) !== JSON.stringify(ownerVals[k]));
  }, [ownerVals, rec]);

  if (loading || !active || !rec) return (
    <div className="p-6 text-xs mono text-muted-foreground"
          data-testid="scanner-policy-loading">Loading…</div>
  );

  const account = rec.portfolio_state?.account || {};
  const buckets = rec.portfolio_state?.buckets || [];
  const globalRec = rec.global_recommendation || {};

  return (
    <div className="p-6 space-y-6 mono" data-testid="scanner-policy-page">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="label-cap">Scanner Policy</div>
          <div className="text-lg mt-1">
            <span className="text-signal-green">{active.policy_version}</span>
            <span className="text-muted-foreground mx-2">·</span>
            <span className="text-muted-foreground text-xs">
              universe {active.universe_version} · status {active.status}
            </span>
          </div>
          <div className="text-[10px] text-muted-foreground mt-1">
            approved {active.approved_at?.slice(0, 16).replace("T", " ")} UTC
            {" · "}source {active.recommendation_source}
            {active.recommendation_type ? ` · ${active.recommendation_type}` : ""}
            {" · pct schema v"}{active.pct_storage_version ?? 1}
          </div>
        </div>
        <button onClick={load} className="chip !text-[10px]"
                data-testid="refresh-recommendation-btn">
          <RefreshCw className="w-3 h-3 inline mr-1" /> REFRESH RECOMMENDATION
        </button>
      </div>

      {/* Account row */}
      <div className="border border-border/60 p-4"
            data-testid="account-context">
        <div className="label-cap mb-2">Account</div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
          <Stat label="Account Equity" value={money(account.equity)}/>
          <Stat label="Available Cash" value={money(account.available_cash)}/>
          <Stat label="Open Positions"
                 value={account.positions_count ?? "—"}/>
          <Stat label="Reference Stop"
                 value={pct(rec.portfolio_state?.reference_stop_pct)}/>
        </div>
      </div>

      {/* Per-bucket recommendations */}
      <div className="border border-border/60"
            data-testid="bucket-recommendations">
        <div className="label-cap p-3 border-b border-border/40">
          Per-Bucket Recommendation
        </div>
        {buckets.length === 0 && (
          <div className="p-4 text-xs text-signal-yellow">
            No active portfolio buckets configured. Recommendation is
            falling back to the price floor. Configure at least one
            bucket to get a capital-aware recommendation.
          </div>
        )}
        {buckets.map((b) => (
          <BucketRow key={b.key} b={b}/>
        ))}
      </div>

      {/* Global system + owner override banner */}
      <div className="border border-signal-green/50 bg-signal-green/5 p-4"
            data-testid="global-recommendation">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
          <Stat label="System Recommended"
                 value={money(globalRec.max_price)}
                 valueClass="text-signal-green text-xl"
                 testid="stat-system-recommended"/>
          <Stat label="Owner Approved"
                 value={money(active.max_price)}
                 valueClass="text-foreground text-xl"
                 testid="stat-owner-approved"/>
          <Stat label="Active Max Price"
                 value={money(active.max_price)}
                 valueClass={
                   active.max_price !== globalRec.max_price
                     ? "text-signal-yellow text-xl" : "text-foreground text-xl"
                 }
                 testid="stat-active"/>
        </div>
        <div className="text-[11px] text-muted-foreground mt-2">
          {globalRec.explanation}
        </div>
        {active.max_price !== globalRec.max_price && (
          <div className="text-[10px] text-signal-yellow mt-2 flex items-center gap-1">
            <AlertTriangle className="w-3 h-3"/>
            OWNER OVERRIDE active: engine recommended
            {" "}<b>${globalRec.max_price}</b>; you approved{" "}
            <b>${active.max_price}</b>. New engine recommendations will
            NOT auto-change this — you must explicitly approve.
          </div>
        )}
      </div>

      {/* Editable policy comparison */}
      <div className="border border-border/60">
        <div className="label-cap p-3 border-b border-border/40">
          Policy Filters (Owner-Approved)
        </div>
        <div className="grid grid-cols-[1.5fr_1fr_1fr_1fr_2fr] gap-x-4 gap-y-2 p-3 text-xs items-center"
              data-testid="policy-comparison">
          <div className="text-[10px] text-muted-foreground uppercase tracking-widest">Setting</div>
          <div className="text-[10px] text-muted-foreground uppercase tracking-widest">Current</div>
          <div className="text-[10px] text-muted-foreground uppercase tracking-widest">System</div>
          <div className="text-[10px] text-muted-foreground uppercase tracking-widest">Owner Value</div>
          <div className="text-[10px] text-muted-foreground uppercase tracking-widest">Reason</div>
          {[
            ["min_price", "Min Price"],
            ["max_price", "Max Stock Price"],
            ["min_avg_dollar_volume", "Min $ Volume"],
            ["min_avg_daily_volume", "Min Daily Volume"],
            ["min_whole_shares_affordable", "Min Whole Shares"],
          ].map(([k, label]) => (
            <ParamRow key={k} label={label} k={k}
              cur={active[k]} recVal={rec.values[k]}
              ownerVal={ownerVals[k]}
              onChange={(v) => setOwnerVals((prev) => ({ ...prev, [k]: v }))}
              reason={rec.reasons[k]}
              overridden={overrideFields.includes(k)}/>
          ))}
        </div>
        <div className="border-t border-border/40 p-3 flex gap-2 flex-wrap">
          <button onClick={() => setOwnerVals({ ...rec.values })}
                  className="chip !text-[10px] hover:border-signal-green"
                  data-testid="use-recommended-btn">
            <Sparkles className="w-3 h-3 inline mr-1"/> USE SYSTEM RECOMMENDED
          </button>
          <button onClick={runPreview}
                  className="chip !text-[10px] hover:border-[#0A84FF]"
                  data-testid="preview-impact-btn">PREVIEW IMPACT</button>
          <button onClick={() => setConfirmOpen(true)}
                  className="chip !text-[10px] bg-signal-green/10 text-signal-green border-signal-green/40 hover:border-signal-green"
                  data-testid="save-policy-btn">SAVE AS NEW POLICY VERSION</button>
          {overrideFields.length > 0 && (
            <span className="chip !text-[10px] text-signal-yellow border-signal-yellow/40 ml-auto">
              <AlertTriangle className="w-3 h-3 inline mr-1"/>
              {overrideFields.length} owner override(s): {overrideFields.join(", ")}
            </span>
          )}
        </div>
      </div>

      {/* Impact preview */}
      {preview && (
        <div className="border border-[#0A84FF]/40 p-4"
              data-testid="impact-preview">
          <div className="label-cap mb-2 text-[#0A84FF]">Policy Impact Preview</div>
          {preview.cache_stale && (
            <div className="text-[10px] text-signal-yellow mb-2">
              Quote cache is empty in this environment — preview uses no
              sample. Run any scheduled scan to populate.
            </div>
          )}
          <div className="grid grid-cols-4 gap-4 text-xs">
            <Stat label="Universe" value={preview.universe_size}/>
            <Stat label="Current Eligible" value={preview.current_eligible}/>
            <Stat label="Proposed Eligible" value={preview.proposed_eligible}/>
            <Stat label="Net Change" value={preview.net_change}
                   valueClass={preview.net_change < 0
                     ? "text-signal-yellow" : "text-signal-green"}/>
          </div>
          <div className="grid grid-cols-2 gap-4 mt-4 text-[11px]">
            <div>
              <div className="text-[10px] text-muted-foreground uppercase tracking-widest mb-1">Newly Excluded</div>
              <div className="flex flex-wrap gap-1">
                {(preview.newly_excluded || []).slice(0, 20).map((s) => (
                  <span key={s} className="chip !text-[9px] text-signal-yellow border-signal-yellow/30">{s}</span>
                ))}
                {!preview.newly_excluded?.length && (
                  <span className="text-muted-foreground">—</span>)}
              </div>
            </div>
            <div>
              <div className="text-[10px] text-muted-foreground uppercase tracking-widest mb-1">Newly Eligible</div>
              <div className="flex flex-wrap gap-1">
                {(preview.newly_eligible || []).slice(0, 20).map((s) => (
                  <span key={s} className="chip !text-[9px] text-signal-green border-signal-green/30">{s}</span>
                ))}
                {!preview.newly_eligible?.length && (
                  <span className="text-muted-foreground">—</span>)}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Forensic lookup */}
      <div className="border border-border/60 p-4">
        <div className="label-cap mb-2">Why wasn&#39;t this stock considered?</div>
        <div className="flex gap-2">
          <input value={whySym} onChange={(e) => setWhySym(e.target.value)}
                 placeholder="Ticker (e.g. NVDA)"
                 className="bg-background border border-border/40 px-2 py-1 text-xs w-40"
                 data-testid="why-excluded-input"/>
          <button onClick={lookupWhy} className="chip !text-[10px]"
                  data-testid="why-excluded-btn">
            <Search className="w-3 h-3 inline mr-1"/>LOOKUP
          </button>
        </div>
        {why && (
          <div className="mt-3 text-xs" data-testid="why-excluded-result">
            <div className="mono text-foreground">{why.symbol}</div>
            <div className="mt-1"><span className="chip !text-[9px]">{why.category}</span></div>
            <div className="text-muted-foreground mt-1 text-[11px]">{why.detail}</div>
          </div>
        )}
      </div>

      {/* History */}
      <div className="border border-border/60 p-4">
        <div className="label-cap mb-2">Policy History</div>
        <table className="w-full text-xs" data-testid="policy-history">
          <thead className="text-[10px] text-muted-foreground uppercase tracking-widest">
            <tr>
              <th className="text-left py-1">Version</th>
              <th className="text-left">Status</th>
              <th className="text-right">Max Price</th>
              <th className="text-right">Min Shares</th>
              <th className="text-left pl-3">Source</th>
              <th className="text-left">Overrides</th>
              <th className="text-left">Approved</th>
            </tr>
          </thead>
          <tbody>
            {history.map((v) => (
              <tr key={v.id} className="border-t border-border/20">
                <td className="py-1 font-mono text-[10px]">{v.id.slice(0, 8)}</td>
                <td className={v.status === "ACTIVE" ? "text-signal-green" : "text-muted-foreground"}>{v.status}</td>
                <td className="text-right">${v.max_price?.toFixed(0)}</td>
                <td className="text-right">{v.min_whole_shares_affordable}</td>
                <td className="pl-3 text-muted-foreground">{v.recommendation_source}</td>
                <td className="text-[10px] text-signal-yellow">
                  {(v.override_fields || []).join(", ") || "—"}
                </td>
                <td className="text-muted-foreground">{v.approved_at?.slice(0, 16).replace("T", " ")}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Save confirm */}
      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent className="mono" data-testid="save-policy-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-signal-green">Replace Active Policy</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="text-xs text-muted-foreground space-y-2 pt-2">
                <div>Current: <span className="text-foreground">{active.policy_version}</span> (id {active.id.slice(0, 8)})</div>
                <div>New: creates a fresh version and activates it.</div>
                <div>Affects future opportunity discovery only. Historical evidence unchanged.</div>
                {overrideFields.length > 0 && (
                  <div className="text-signal-yellow">
                    Owner overrides vs system recommendation: {overrideFields.join(", ")}
                  </div>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="save-policy-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={activate} data-testid="save-policy-confirm-btn"
              className="bg-signal-green text-background hover:bg-signal-green/90">
              <Check className="w-3 h-3 inline mr-1"/> Approve & Activate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}


function BucketRow({ b }) {
  const rows = [
    ["Bucket Capital", money(b.allocated_capital)],
    ["Available Cash", money(b.available_cash)],
    ["Max Position %", pct(b.max_position_pct)],
    ["Max Position Budget", money(b.max_position_budget)],
    ["Max Risk / Trade %", pct(b.max_risk_per_trade_pct)],
    ["Per-Trade Loss Budget", money(b.per_trade_loss_budget)],
    ["Reference Stop %", pct(b.reference_stop_pct)],
    ["Risk-Limited Budget", money(b.risk_limited_budget)],
  ];
  return (
    <div className="border-t border-border/40 p-3"
          data-testid={`bucket-row-${b.key}`}>
      <div className="flex items-baseline justify-between">
        <div>
          <span className="text-foreground text-sm">{b.name}</span>
          <span className="text-[10px] text-muted-foreground ml-2 uppercase tracking-widest">
            {b.key}
          </span>
        </div>
        <div className="text-right">
          <div className="text-[10px] text-muted-foreground uppercase tracking-widest">
            Recommended Max Stock Price
          </div>
          <div className="text-signal-green text-xl mt-0.5"
                data-testid={`bucket-${b.key}-max-price`}>
            ${b.recommended_max_price?.toLocaleString(undefined, { maximumFractionDigits: 0 })}
          </div>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-1 text-[11px] mt-3">
        {rows.map(([k, v]) => (
          <div key={k}>
            <span className="text-muted-foreground">{k}: </span>
            <span className="text-foreground">{v}</span>
          </div>
        ))}
        <div>
          <span className="text-muted-foreground">Min Whole Shares: </span>
          <span className="text-foreground">{b.min_whole_shares_affordable}</span>
        </div>
        <div>
          <span className="text-muted-foreground">Deployable / Position: </span>
          <span className="text-foreground">{money(b.deployable_per_position)}</span>
        </div>
        <div className="col-span-2">
          <span className="text-muted-foreground">Bound by: </span>
          <span className="text-foreground">{b.binding_rule || "—"}</span>
        </div>
      </div>
      <div className="text-[10px] text-muted-foreground mt-2 flex items-start gap-1">
        <Info className="w-3 h-3 mt-[1px] flex-none"/>
        <span>{b.explanation}</span>
      </div>
    </div>
  );
}


function ParamRow({ label, k, cur, recVal, ownerVal, onChange, reason, overridden }) {
  const isNum = typeof recVal === "number";
  return (
    <>
      <div className="text-foreground">{label}</div>
      <div className="text-muted-foreground">{fmt(cur, k)}</div>
      <div className="text-signal-green">{fmt(recVal, k)}</div>
      <div>
        <input
          type={isNum ? "number" : "text"}
          value={ownerVal}
          onChange={(e) => onChange(isNum ? Number(e.target.value) : e.target.value)}
          className={`bg-background border px-2 py-1 text-xs w-28 ${overridden ? "border-signal-yellow" : "border-border/40"}`}
          data-testid={`owner-input-${k}`}
        />
      </div>
      <div className="text-[10px] text-muted-foreground">{reason}</div>
    </>
  );
}


function Stat({ label, value, valueClass = "text-foreground", testid }) {
  return (
    <div data-testid={testid}>
      <div className="text-[10px] text-muted-foreground uppercase tracking-widest">{label}</div>
      <div className={`mt-1 text-lg ${valueClass}`}>{value}</div>
    </div>
  );
}
